import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Clock, CheckCircle2, XCircle, Loader2, Hourglass } from "lucide-react";
import { openReceipt } from "@/lib/receipt";

const PAYMENTS = ["Efectivo", "Nequi", "Daviplata", "Transferencia", "Tarjeta"];

function remaining(expiresAt, now) {
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return null;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function SellerReservations({ refreshKey, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [pay, setPay] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/reservations/mine");
      setRows(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh once when a reservation expires so the map/sales stay in sync.
  const expiredHandled = useRef(false);
  useEffect(() => {
    const anyExpired = rows.some((r) => remaining(r.expires_at, now) === null);
    if (anyExpired && !expiredHandled.current) {
      expiredHandled.current = true;
      load();
      onChange?.();
    } else if (!anyExpired) {
      expiredHandled.current = false;
    }
  }, [now, rows, load, onChange]);

  const confirm = async (ficha) => {
    setBusy(ficha);
    try {
      const { data: sale } = await api.post(`/reservations/${ficha}/confirm`, { payment_method: pay[ficha] || "Efectivo" });
      toast.success(`Venta confirmada · Ficha #${String(ficha).padStart(4, "0")}`, {
        description: "Envía el comprobante al comprador",
        action: { label: "Comprobante WhatsApp", onClick: () => openReceipt(sale) },
        duration: 12000,
      });
      load();
      onChange?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const cancel = async (ficha) => {
    setBusy(ficha);
    try {
      await api.delete(`/reservations/${ficha}`);
      toast(`Reserva #${ficha} cancelada`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const active = rows.filter((r) => remaining(r.expires_at, now) !== null);

  return (
    <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 overflow-hidden" data-testid="reservations-section">
      <div className="px-6 py-4 border-b border-[#F0B91B]/15">
        <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
          <Hourglass className="w-5 h-5 text-[#F0B91B]" /> Reservas activas
        </h2>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 text-[#F0B91B] animate-spin" /></div>
      ) : active.length === 0 ? (
        <div className="p-8 text-center text-[#FDFBF7]/50 font-outfit text-sm" data-testid="no-reservations">
          No tienes reservas activas. Reserva un número mientras el cliente paga.
        </div>
      ) : (
        <div className="divide-y divide-[#F0B91B]/8">
          {active.map((r) => {
            const time = remaining(r.expires_at, now);
            const urgent = time && Number(time.split(":")[0]) < 5;
            return (
              <div key={r.ficha_number} className="px-6 py-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`reservation-${r.ficha_number}`}>
                <div className="flex items-center gap-4">
                  <div className="font-bebas text-3xl text-[#F0B91B] tracking-wider">#{r.ficha_number}</div>
                  <div>
                    <div className="text-sm text-[#FDFBF7] font-outfit">{r.buyer_name || "Sin comprador"}</div>
                    <div className={`text-xs font-outfit inline-flex items-center gap-1 ${urgent ? "text-[#E63946]" : "text-[#FDFBF7]/60"}`}>
                      <Clock className="w-3.5 h-3.5" /> Expira en <strong className="font-bebas text-sm tracking-wider" data-testid={`countdown-${r.ficha_number}`}>{time}</strong>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={pay[r.ficha_number] || "Efectivo"}
                    onChange={(e) => setPay({ ...pay, [r.ficha_number]: e.target.value })}
                    className="bg-[#051A0F] border border-[#F0B91B]/20 rounded-lg px-3 py-2 text-[#FDFBF7] font-outfit text-xs focus:outline-none focus:border-[#F0B91B]"
                    data-testid={`res-payment-${r.ficha_number}`}
                  >
                    {PAYMENTS.map((p) => <option key={p} value={p} className="bg-[#0A2818]">{p}</option>)}
                  </select>
                  <button
                    onClick={() => confirm(r.ficha_number)}
                    disabled={busy === r.ficha_number}
                    className="inline-flex items-center gap-1.5 bg-[#7FD1AE] hover:bg-[#6BC49E] disabled:opacity-60 text-[#051A0F] px-4 py-2 rounded-lg font-bebas tracking-wider text-sm transition-colors"
                    data-testid={`confirm-${r.ficha_number}`}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Confirmar venta
                  </button>
                  <button
                    onClick={() => cancel(r.ficha_number)}
                    disabled={busy === r.ficha_number}
                    className="inline-flex items-center gap-1.5 text-[#E63946] hover:bg-[#E63946]/10 px-3 py-2 rounded-lg font-outfit text-sm transition-colors"
                    data-testid={`cancel-${r.ficha_number}`}
                  >
                    <XCircle className="w-4 h-4" /> Cancelar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
